<template>
  <FilterOverview :showFilterInitially="false" title="Reisefinder" @reload="onReload">
    <template #title-right>
      <span
        class="ml-4 d-inline-flex"
        v-b-tooltip.hover="{ customClass: 'reisefinder-info-tooltip' }"
        :title="reisefinderTitleHoverText"
      >
        <Info :size="24" color="#B5B5C3" />
      </span>
    </template>
    <template #toolbar-left>
      <button
        type="button"
        class="icon-button filter-button d-flex justify-content-center p-0 mr-4"
        :class="{ 'filter-button--aktiv': checkIfFilterSupplied }"
        v-b-tooltip.hover.bottom
        title="Filter anpassen"
        @click="onClickFilterButton"
        @keyup="handleKeyDown"
      >
        <span class="d-flex align-items-center"><Funnel :size="20" /></span>
      </button>
      <b-form-checkbox
        class="mr-5"
        v-model="ausgebuchteTermineCheckbox"
        @change="ausgebuchteTermineChanged"
        switch
      >
        Ausgebuchte Termine anzeigen
      </b-form-checkbox>

      <!-- Filter Reset Button -->
      <div
        v-if="checkIfFilterSupplied"
        class="icon-button d-flex justify-content-center text-danger mr-4"
        @click="resetAllFilters"
        v-b-tooltip.hover.bottom
        title="Filter zurücksetzen"
      >
        <span class="d-flex align-items-center"><FunnelX :size="20" /></span>
      </div>

      <!-- Reisefinder Filter -->
      <b-modal
        id="modal-reisefinder"
        :visible="filterActive"
        @show="merkeFilterStand"
        @hide="filterActive = false"
        @shown="focusReisekuerzelFilter"
        size="lg"
        centered
        title="Filter Reisefinder"
      >
        <div class="reisefinder-filter" @keydown="onFilterTabKeydown">
          <div class="reisefinder-filter__zeile">
            <div>
              <FeldLabel class="mb-2" :icon="icons.Hash" text="Reisekürzel" />
              <AppSelect
                ref="reisekuerzelFilter"
                label="Reisekürzel"
                search-placeholder="Reisekürzelsuche"
                multiple
                :loading="reiseterminSearchLoading"
                :options="reisekuerzelOptions"
                :value="inputReiseterminkuerzel"
                :state="inputReiseterminkuerzel.length > 0 ? true : null"
                @input="changeReisekuerzel($event)"
                @search-change="onSearchChangeReisetermin"
              />
            </div>
            <div>
              <FeldLabel class="mb-2" :icon="icons.Type" text="Reisetitel" />
              <AppSelect
                class="reisetitel-select"
                label="Reisetitel"
                search-placeholder="Reisetitel suchen …"
                multiple
                :internal-search="false"
                :loading="reisetitelSearchLoading"
                :options="reisetitelOptions"
                :value="inputReisetitel"
                :state="inputReisetitel.length > 0 ? true : null"
                @input="changeReisetitel($event)"
                @search-change="onSearchChangeReisetitel"
              />
            </div>
          </div>

          <div class="reisefinder-filter__zeile">
            <div>
              <FeldLabel class="mb-2" :icon="icons.PlaneTakeoff" text="Frühester Abflug" />
              <AppDatePicker
                v-model="dateAbflug"
                placeholder="Frühester Abflug"
                format="DD.MM.YY"
                :max="dateAnkunft"
                :state="dateAbflug !== null ? true : null"
              />
            </div>
            <div>
              <FeldLabel class="mb-2" :icon="icons.PlaneLanding" text="Späteste Ankunft" />
              <AppDatePicker
                v-model="dateAnkunft"
                placeholder="Späteste Ankunft"
                format="DD.MM.YY"
                :min="dateAbflug"
                :state="dateAnkunft !== null ? true : null"
              />
            </div>
          </div>

          <FilterRegionCountry
            :countriesGroupedByRegions="countriesGroupedByRegions"
            :valueRegion="regionSelected"
            :valueCountry="countrySelected"
            @inputRegion="setRegionFilter"
            @inputCountry="setCountryFilter"
          />

          <div class="reisefinder-filter__zeile">
            <div>
              <FeldLabel class="mb-2" :icon="icons.Route" text="Reiseart" />
              <AppSelect
                id="filter-rundaufenthaltsreise"
                label="Reiseart"
                placeholder="Alle"
                sort="none"
                clearable
                hide-search
                :options="rundAufenthaltsreiseOptions"
                v-model="rundAufenthaltsreiseSelected"
                :state="rundAufenthaltsreiseSelected !== null ? true : null"
              />
            </div>
            <div>
              <FeldLabel class="mb-2" :icon="icons.ShieldCheck" text="Garantierte Reisetermine" />
              <AppSelect
                id="filter-garantiert"
                label="Garantierte Reisetermine"
                placeholder="Alle"
                sort="none"
                clearable
                hide-search
                :options="garantiertFilterOptions"
                v-model="garantiertFilterSelected"
                :state="garantiertFilterSelected !== null ? true : null"
              />
            </div>
          </div>

          <div class="reisefinder-filter__zeile">
            <div>
              <FeldLabel class="mb-2" :icon="icons.Users" text="Interessenten" />
              <div class="d-flex">
                <div class="reisefinder-stepper">
                  <button type="button" tabindex="-1" class="reisefinder-stepper__btn" @click="stepPax(-1)">
                    <Minus :size="16" />
                  </button>
                  <b-form-input
                    class="reisefinder-stepper__input"
                    title="PAX"
                    aria-label="PAX"
                    type="number"
                    v-model.number="pax"
                  ></b-form-input>
                  <button type="button" tabindex="-1" class="reisefinder-stepper__btn" @click="stepPax(1)">
                    <Plus :size="16" />
                  </button>
                </div>
                <button
                  v-for="anzahl in [1, 2]"
                  :key="anzahl"
                  type="button"
                  tabindex="-1"
                  class="chip reisefinder-stepper__schnellwahl ml-2"
                  :class="{ 'chip--active': pax === anzahl }"
                  :title="anzahl === 1 ? '1 Interessent' : '2 Interessenten'"
                  @click="pax = anzahl"
                >
                  {{ anzahl }}
                </button>
              </div>
            </div>
            <div>
              <FeldLabel class="mb-2" :icon="icons.Euro" text="Max. Budget p. P." />
              <div
                class="reisefinder-stepper"
                :class="{ 'reisefinder-stepper--aktiv': inputMaxBudgetProPerson }"
              >
                <button type="button" tabindex="-1" class="reisefinder-stepper__btn" @click="stepBudget(-1)">
                  <Minus :size="16" />
                </button>
                <b-form-input
                  class="reisefinder-stepper__input"
                  placeholder="in €"
                  type="number"
                  :value="inputMaxBudgetProPerson"
                  @input="inputMaxBudgetProPerson = $event === '' ? null : $event"
                ></b-form-input>
                <button type="button" tabindex="-1" class="reisefinder-stepper__btn" @click="stepBudget(1)">
                  <Plus :size="16" />
                </button>
              </div>
            </div>
          </div>

          <div
            class="reisefinder-switch-box"
            :class="{ 'reisefinder-switch-box--aktiv': zimmerkontingentBeruecksichtigen }"
            @click.self="zimmerkontingentBeruecksichtigen = !zimmerkontingentBeruecksichtigen"
          >
            <label for="filter-durchbuchbar" class="reisefinder-switch-box__text">
              Nur durchbuchbare Termine anzeigen
            </label>
            <b-form-checkbox id="filter-durchbuchbar" v-model="zimmerkontingentBeruecksichtigen" switch />
          </div>
        </div>
        <template #modal-footer>
          <b-button class="dialog-btn-secondary" @click="filterAbbrechen">Abbrechen</b-button>
          <b-button
            class="dialog-btn"
            variant="primary"
            :disabled="!checkIfFilterSupplied"
            @click="filterAnwenden"
          >
            Anwenden
          </b-button>
        </template>
      </b-modal>
    </template>
    <template #table="{ tableHeight }">
      <ag-grid-vue
        :style="{ height: tableHeight + 87 + 'px' }"
        class="ag-theme-alpine m-0 p-0"
        :columnDefs="columnDefs"
        :getRowHeight="getRowHeight"
        :rowModelType="rowModelType"
        :statusBar="statusBar"
        :tooltipShowDelay="0"
        :autoSizeStrategy="autoSizeStrategy"
        :defaultColDef="defaultColDef"
        :masterDetail="true"
        :detailCellRenderer="'DetailCellRenderer'"
        :detailRowAutoHeight="'true'"
        :enableCellTextSelection="true"
        suppressContextMenu
        @grid-ready="onGridReady"
        @rowClicked="onRowClicked"
        suppressCellFocus
      ></ag-grid-vue>
      <AufgabeErstellenModal
        :prefixedVerlinkungen="prefixedVerlinkungen"
        :disableAttachments="false"
        :enableManualTask="true"
      />
    </template>
  </FilterOverview>
</template>

<script>
import AppDatePicker from '@/components/common/app-date-picker/app-date-picker.vue';
import FilterRegionCountry from '@/components/produkte/reisetermine/filter-region-country.vue';
import { AgGridVue } from 'ag-grid-vue';
import 'ag-grid-enterprise';
import FilterOverview from '@/components/common/filter-overview.vue';
import CellRendererZimmerkontingente from '@/components/common/zimmerkontingente/cell-renderer-zimmerkontingente.vue';
import { mapGetters, mapState } from 'vuex';
import { GET_REISETERMINE } from '@/core/produkte/reisetermine/stores/reisefinder.module';
import StatusBarComponent from '@/components/flugverfuegbarkeit/status-bar-ag-grid.vue';
import { differenceInDays, parseISO, format, subDays, differenceInCalendarDays } from 'date-fns';
import CellRendererPax from '@/components/produkte/reisetermine/cell-renderer-pax.vue';
import CellRendererActionButtons from '@/components/produkte/reisetermine/cell-renderer-action-buttons.vue';
import AufgabeErstellenModal from '@/components/aufgaben/aufgabe-erstellen-modal.vue';
import CellRendererFlugverfuegbarkeiten from '@/components/produkte/reisetermine/cell-renderer-flugverfuegbarkeiten.vue';
import CustomHeader from '@/components/flugverfuegbarkeit/custom-header.vue';
import CustomTooltip from '@/components/flugverfuegbarkeit/custom-tooltip.vue';

import { GET_ALL_COUNTRIES_GROUPED_BY_REGIONS } from '@/core/produkte/reisen/stores/reisen.module';
import CellRendererBewertung from '@/components/produkte/reisetermine/cell-renderer-bewertung.vue';
import AppSelect from '@/components/common/app-select/app-select.vue';
import { openAppSelectTrigger } from '@/components/common/app-select/utils';
import DateRangePicker from '@/components/common/date-range-picker.vue';
import buildQuery from 'odata-query';
import apiService from '@/core/common/services/api.service';
import * as applicationInsights from '@/core/common/services/application-insights.service';
import DetailCellRenderer from '@/components/produkte/reisetermine/details-reisefinder.vue';
import { useDebounceFn } from '@vueuse/core';
import { de } from 'date-fns/locale';
import {
  Funnel,
  FunnelX,
  Info,
  Hash,
  Type,
  PlaneTakeoff,
  PlaneLanding,
  Route,
  Users,
  Euro,
  ShieldCheck,
  Minus,
  Plus,
  CircleCheck,
  CircleX,
  StarHalf,
} from 'lucide-vue';
import { lucideHtml } from '@/core/common/helpers/lucide-html';
import FeldLabel from '@/components/common/feld-label.vue';
import { reiseCountryName } from '@/core/common/iso3166Countries.js';

export default {
  name: 'App',
  components: {
    AppDatePicker,
    AgGridVue,
    FilterRegionCountry,
    FilterOverview,
    DateRangePicker,
    DetailCellRenderer,
    CellRendererZimmerkontingente,
    StatusBarComponent,
    CellRendererPax,
    CellRendererActionButtons,
    AppSelect,
    CellRendererFlugverfuegbarkeiten,
    CustomHeader,
    CustomTooltip,
    CellRendererBewertung,
    Funnel,
    FunnelX,
    Info,
    Minus,
    Plus,
    FeldLabel,
    AufgabeErstellenModal,
  },
  data() {
    return {
      // Icons der FeldLabels im Filtermodal, werden über :icon als Komponente übergeben
      icons: { Hash, Type, PlaneTakeoff, PlaneLanding, Route, Users, Euro, ShieldCheck },
      reisefinderTitleHoverText:
        'Der Reisefinder dient als primäres Informationstool für das Sales Team. Es werden alle buchbaren Termine in der Zukunft angezeigt. ',
      dateAbflug: null,
      dateAnkunft: null,
      columnDefs: null,
      prefixedVerlinkungen: [],
      defaultColDef: {
        cellStyle: { display: 'flex', alignItems: 'center' },
        suppressMenu: true,
        resizable: false,
        floatingFilterComponentParams: {
          suppressFilterButton: true,
        },
        suppressMovable: true,
        filterParams: {
          suppressFilterButton: true,
        },
        tooltipComponent: 'CustomTooltip',
      },
      statusBar: {
        statusPanels: [{ statusPanel: 'StatusBarComponent', key: 'statusBarCompKey', align: 'left' }],
      },
      filterActive: true,
      filterStandBeimOeffnen: null,
      regionSelected: [],
      countrySelected: [],
      rundAufenthaltsreiseSelected: null,
      rundAufenthaltsreiseOptions: ['Rundreise', 'Aufenthaltsreise'],
      rowModelType: null,
      inputReiseterminkuerzel: [],
      inputReisetitel: [],
      pax: 1,
      zimmerkontingentBeruecksichtigen: false,
      inputMaxBudgetProPerson: null,
      garantiertFilterSelected: null,
      garantiertFilterOptions: ['Garantiert', 'Nicht garantiert'],
      reisekuerzelOptions: [],
      timeoutReiseterminkuerzelSearch: null,
      timeoutReisetitelSearch: null,
      allReisekuerzel: [],
      reisetitelOptions: [],
      reisetitelSearchLoading: false,
      reiseterminSearchLoading: false,
      autoSizeStrategy: null,
      ausgebuchteTermineCheckbox: false,
      debouceTest: null,
    };
  },
  created() {
    if (!this.countriesGroupedByRegions) {
      this.$store.dispatch(GET_ALL_COUNTRIES_GROUPED_BY_REGIONS);
    }

    this.debouceTest = useDebounceFn(async queryString => {
      try {
        const res = await apiService.get(
          `Reise${queryString}&$expand=reisetermine($filter=abreisedatum ge now();$count=true;$top=0)&$select=reisekuerzel,titel,id`
        );

        this.reisetitelSearchLoading = false;
        const reisenWithFutureReiseterminen = res.data.result.value.filter(
          reise => reise['reisetermine@odata.count'] > 0
        );
        const reisen = reisenWithFutureReiseterminen.length > 0 ? reisenWithFutureReiseterminen : [];
        this.reisetitelOptions = reisen.map(reise => reise.titel);
      } catch (err) {
        console.error(err);
        this.reisetitelSearchLoading = false;
      }
    }, 500);

    this.rowModelType = 'serverSide';
    this.columnDefs = [
      {
        headerName: 'Reise',
        field: 'reise.reisekuerzel',
        filter: 'agTextColumnFilter',
        cellRenderer: params => {
          const reisekuerzel = params.data.reise?.reisekuerzel;
          const istZusatztermin = params.data.istZusatztermin ? '<b>Z</b>' : '';
          return `${reisekuerzel} ${istZusatztermin}`;
        },
        minWidth: 90,
        width: 90,
        tooltipField: 'reise.titel',
        pinned: 'left',
        sortable: true,
        headerComponent: 'CustomHeader',
      },
      {
        headerName: 'Reisetitel',
        field: 'reise.titel',
        filter: 'agTextColumnFilter',
        hide: true,
      },
      {
        headerName: '',
        field: 'reise.bewertungenZusammenfassung.durchschnittZufriedenheit',
        filter: false,
        pinned: 'left',
        sortable: false,
        minWidth: 40,
        width: 40,
        headerComponent: 'CustomHeader',
        headerComponentParams: {
          iconComponent: StarHalf,
          centered: true,
        },
        cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'center' },
        cellRenderer: 'CellRendererBewertung',
      },
      {
        headerName: 'Abflug DE',
        field: 'abreisedatum',
        cellRenderer: params => {
          const datum = this.dateFormatterStandard(params);
          const nameOfDate = format(new Date(params.value), 'EEEEEE', { locale: de });
          return `<span>${datum} <span style="color: gray; font-style: italic;">${nameOfDate}</span></span>`;
        },
        minWidth: 140,
        width: 140,
        pinned: 'left',
        sort: 'asc',
        sortable: true,
        headerComponent: 'CustomHeader',
      },
      {
        headerName: 'Ankunft DE',
        field: 'enddatum',
        cellRenderer: params => {
          const datum = this.dateFormatterStandard(params);
          const nameOfDate = format(new Date(params.value), 'EEEEEE', { locale: de });
          return `<span>${datum} <span style="color: gray; font-style: italic;">${nameOfDate}</span></span>`;
        },
        minWidth: 140,
        width: 140,
        pinned: 'left',
        sortable: true,
        headerComponent: 'CustomHeader',
      },
      { headerName: 'Dauer', field: 'reisedauer', minWidth: 80, width: 80 },
      {
        headerName: 'Gar.',
        headerTooltip: 'Garantiert',
        field: 'garantiert',
        cellRenderer: params => {
          const { value } = params;
          if (value) {
            return lucideHtml(CircleCheck, { size: 20, class: 'text-success' });
          } else {
            return lucideHtml(CircleX, { size: 20, class: 'text-danger' });
          }
        },
        minWidth: 70,
        width: 70,
      },
      {
        headerName: 'TbR',
        headerTooltip: 'Tage bis Release',
        field: 'releasedatum',
        cellRenderer: params => this.calculateReleaseDays(params),
        tooltipValueGetter: params => {
          const CalculatedDays = this.calculateReleaseDays(params);
          if (CalculatedDays.toString().length > 9) {
            return CalculatedDays;
          } else {
            return null;
          }
        },
        minWidth: 125,
        width: 125,
      },
      {
        field: 'pax',
        headerName: 'PAX',
        cellRenderer: 'CellRendererPax',
        minWidth: 170,
        width: 170,
        filter: false,
        suppressRowTransform: true,
        cellStyle: {
          'line-height': 'normal',
          display: 'flex',
          'justify-items': 'center',
          overflow: 'visible',
          'align-items': 'center',
        },
        cellRendererParams: params => ({
          paxObject: params.data.pax,
          minPax: params.data.metadata.minPax,
          maxPax: params.data.metadata.maxPax,
          kalkPax: params.data.metadata.kalkPax,
          forecastPax: params.data.metadata.forecastPax,
          durchfuehrbareMinPax: params.data.metadata.durchfuehrbareMinPax,
          id: params.data.id,
        }),
      },
      {
        headerName: 'Zimmerkontingente',
        field: 'zimmerkontingente',
        cellRenderer: 'CellRendererZimmerkontingente',
        width: 210,
        minWidth: 210,
        cellStyle: {
          'line-height': 'normal',
          display: 'flex',
          'align-items': 'center',
          'justify-items': 'center',
          overflow: 'visible',
        },
      },
      {
        headerName: 'DZ p.P.',
        field: 'basisPreis',
        filter: 'agNumberColumnFilter',
        icons: false,
        cellRenderer: params => (params.value ? this.$options.filters.currency(params.value) : '-'),
        width: 120,
      },
      {
        headerName: 'EZZ p. P.',
        headerTooltip: 'Einzelzimmerzuschlag pro Person',
        field: 'einzelzimmerzuschlag',
        cellRenderer: params => this.getEinzelzimmerzuschlag(params.data),
        width: 120,
      },
      {
        headerName: 'Flugverfügbarkeit',
        field: 'flugverfuegbarkeiten',
        cellRenderer: 'CellRendererFlugverfuegbarkeiten',
        cellRendererParams: {
          excludeAirports: ['GVA'],
        },
        width: 450,
        minWidth: 450,
        flex: 1,
      },
      {
        headerName: 'Aktionen',
        cellRenderer: 'CellRendererActionButtons',
        cellRendererParams: {
          onShowModalAufgabeEdit: this.onShowModalAufgabeEdit,
        },
        minWidth: 172,
        width: 172,
        pinned: 'right',
      },
      {
        headerName: 'Land',
        field: 'reise.country',
        filter: 'agSetColumnFilter',
        filterParams: {
          values: params => {
            params.success(this.countriesFromStore());
          },
          cellRenderer: params => (params.value ? reiseCountryName(params.value) : '-'),
        },
        hide: true,
      },
      {
        headerName: 'Region',
        field: 'reise.region',
        filter: 'agSetColumnFilter',
        filterParams: {
          values: params => {
            params.success(this.regionsFromStore());
          },
        },
        hide: true,
      },
      {
        field: 'reiseterminkuerzel',
        filter: 'agTextColumnFilter',
        hide: true,
      },
      {
        field: 'reisefinderDate',
        filter: 'agDateColumnFilter',
        hide: true,
      },
    ];
  },
  mounted() {
    window.addEventListener('keydown', this.handleKeyDown);
    this.getReisekuerzel();
  },
  beforeDestroy() {
    window.removeEventListener('keydown', this.handleKeyDown);
  },
  computed: {
    ...mapGetters(['currentUserName', 'parsedRoles']),
    ...mapState({
      reisetermine: state => state.reisefinder.reisetermine,
      count: state => state.reisefinder.count,
      isBusy: state => state.reisefinder.isBusy,
      countriesGroupedByRegions: state => state.reisen.countriesGroupedByRegions,
    }),
    checkIfFilterSupplied() {
      return !(
        this.regionSelected.length === 0 &&
        this.countrySelected.length === 0 &&
        this.garantiertFilterSelected === null &&
        this.dateAnkunft === null &&
        this.dateAbflug === null &&
        this.rundAufenthaltsreiseSelected === null &&
        this.inputReiseterminkuerzel.length === 0 &&
        this.inputReisetitel.length === 0 &&
        this.zimmerkontingentBeruecksichtigen === false &&
        this.inputMaxBudgetProPerson === null
      );
    },
  },
  watch: {
    // wird benötigt, damit die countries und regions beim kopieren der url auch übernommen werden, da diese async laden
    countriesGroupedByRegions: {
      handler(newValue) {
        if (newValue && window.location.hash.includes('?')) {
          this.parseUrlParameters();
          this.$nextTick(() => {
            if (this.checkIfFilterSupplied && this.gridApi) {
              this.applyCurrentFiltersToGrid();
            }
          });
        }
      },
      immediate: false,
    },
  },
  methods: {
    onShowModalAufgabeEdit(prefixedVerlinkungen) {
      this.prefixedVerlinkungen = prefixedVerlinkungen;
      this.$bvModal.show('modalAufgabeEdit');
    },
    parseUrlParameters() {
      const hashParts = window.location.hash.split('?');
      // Wenn nur die base url verwendet wird "#/reisetermine/reisefinder"
      if (hashParts.length < 2) return;

      // wenn min 1 parameter gefilter wird geht es hier weiter
      const urlParams = new URLSearchParams(hashParts[1]);

      // format: mit komma getrennt -> in der URL steht dann "%2C"
      if (urlParams.has('reisekuerzel')) {
        const reisekuerzel = urlParams.get('reisekuerzel').split(',');
        this.inputReiseterminkuerzel = reisekuerzel.map(reiseKuerzel => reiseKuerzel.trim());
      }

      if (urlParams.has('reisetitel')) {
        const reisetitel = urlParams.get('reisetitel').split(',');
        this.inputReisetitel = reisetitel.map(titel => titel.trim());
      }

      // format: yyyy-mm-dd
      if (urlParams.has('fruehesteabflug')) {
        this.dateAbflug = new Date(urlParams.get('fruehesteabflug'));
      }

      // format: yyyy-mm-dd
      if (urlParams.has('spaetesteankunft')) {
        this.dateAnkunft = new Date(urlParams.get('spaetesteankunft'));
      }

      // format: Number
      if (urlParams.has('interessenten')) {
        this.pax = parseInt(urlParams.get('interessenten'));
      }

      // format: mit komma getrennt -> in der URL steht dann "%2C"
      if (urlParams.has('kontinente')) {
        const kontinente = urlParams.get('kontinente').split(',');
        this.setRegionsFromUrlParams(kontinente);
      }

      // format: mit komma getrennt -> in der URL steht dann "%2C"
      if (urlParams.has('laender')) {
        const laender = urlParams.get('laender').split(',');
        this.setCountriesFromUrlParams(laender);
      }
    },

    setRegionsFromUrlParams(kontinenteNames) {
      if (!this.countriesGroupedByRegions) return;

      // Es wird nach dem passenden Key im store für die region gesucht
      this.regionSelected = kontinenteNames
        .map(name =>
          Object.keys(this.countriesGroupedByRegions).find(
            key => key.toLowerCase() === name.trim().toLowerCase()
          )
        )
        // enternt null werte
        .filter(Boolean);
    },

    setCountriesFromUrlParams(laenderNames) {
      if (!this.countriesGroupedByRegions) return;

      // Alle countries aus der ausgewählten region
      const allCountries = Object.values(this.countriesGroupedByRegions).flat();

      // Es wird nach dem passenden Key im store für das Land gesucht
      this.countrySelected = laenderNames
        .map(
          name =>
            allCountries.find(country => country.country.toLowerCase() === name.trim().toLowerCase())?.country
        )
        // enternt null werte
        .filter(Boolean);
    },

    updateUrlWithFilters() {
      const params = new URLSearchParams();

      if (this.inputReiseterminkuerzel && this.inputReiseterminkuerzel.length > 0) {
        params.set('reisekuerzel', this.inputReiseterminkuerzel.join(','));
      }

      if (this.inputReisetitel && this.inputReisetitel.length > 0) {
        params.set('reisetitel', this.inputReisetitel.join(','));
      }

      if (this.dateAbflug) {
        params.set('fruehesteabflug', format(this.dateAbflug, 'yyyy-MM-dd'));
      }

      if (this.dateAnkunft) {
        params.set('spaetesteankunft', format(this.dateAnkunft, 'yyyy-MM-dd'));
      }

      if (this.pax > 1) {
        params.set('interessenten', this.pax.toString());
      }

      if (this.regionSelected && this.regionSelected.length > 0) {
        params.set('kontinente', this.regionSelected.join(','));
      }

      if (this.countrySelected && this.countrySelected.length > 0) {
        params.set('laender', this.countrySelected.join(','));
      }

      const currentHash = window.location.hash.split('?')[0];
      let urlString = params.toString();

      // Dekodiere URL-Parameter damit echte Kommas in der URL angezeigt werden
      if (urlString) {
        urlString = decodeURIComponent(urlString);
      }

      const newUrl = urlString ? `${currentHash}?${urlString}` : currentHash;

      if (window.location.hash !== newUrl) {
        history.replaceState(null, null, newUrl);
      }
    },

    applyCurrentFiltersToGrid() {
      if (!this.gridApi) return;

      const regionFilterInstance = this.gridApi?.getFilterInstance('reise.region');
      const regions = this.regionSelected;
      if (regions.length === 0) {
        regionFilterInstance.setModel(null);
      } else {
        regionFilterInstance.setModel({ values: regions });
      }

      const countryFilterInstance = this.gridApi?.getFilterInstance('reise.country');
      const countries = this.countrySelected;
      if (countries.length === 0) {
        countryFilterInstance.setModel(null);
      } else {
        countryFilterInstance.setModel({ values: countries });
      }

      // add rundreise filter
      const reisekuerzelFilterInstance = this.gridApi.getFilterInstance('reise.reisekuerzel');
      if (this.rundAufenthaltsreiseSelected) {
        reisekuerzelFilterInstance.setModel({
          filter: this.rundAufenthaltsreiseSelected,
        });
      } else {
        reisekuerzelFilterInstance.setModel(null);
      }

      // add reiseterminfilter
      const reiseterminkuerzelFilterInstance = this.gridApi.getFilterInstance('reiseterminkuerzel');
      if (this.inputReiseterminkuerzel) {
        const valuesReisekuerzel = this.inputReiseterminkuerzel;
        reiseterminkuerzelFilterInstance.setModel({
          filter: valuesReisekuerzel.join(','),
        });
      } else {
        reiseterminkuerzelFilterInstance.setModel(null);
      }

      const reisetitelFilterInstance = this.gridApi.getFilterInstance('reise.titel');
      if (this.inputReisetitel) {
        const valuesReisetitel = this.inputReisetitel;
        reisetitelFilterInstance.setModel({
          filter: valuesReisetitel,
        });
      } else {
        reisetitelFilterInstance.setModel(null);
      }

      const dateRangeFilterInstance = this.gridApi.getFilterInstance('reisefinderDate');
      if (this.dateAbflug !== null && this.dateAnkunft !== null) {
        dateRangeFilterInstance.setModel({
          dateFrom: format(this.dateAbflug, 'yyyy-MM-dd'),
          dateTo: format(this.dateAnkunft, 'yyyy-MM-dd'),
          filterType: 'date',
          type: 'inRange',
        });
      } else if (this.dateAbflug !== null && this.dateAnkunft === null) {
        dateRangeFilterInstance.setModel({
          dateFrom: format(this.dateAbflug, 'yyyy-MM-dd'),
          dateTo: '2028-01-01',
          filterType: 'date',
          type: 'inRange',
        });
      } else if (this.dateAbflug === null && this.dateAnkunft !== null) {
        dateRangeFilterInstance.setModel({
          dateFrom: format(new Date(), 'yyyy-MM-dd'),
          dateTo: format(this.dateAnkunft, 'yyyy-MM-dd'),
          filterType: 'date',
          type: 'inRange',
        });
      } else {
        dateRangeFilterInstance.setModel(null);
      }

      const maxBudgetProPersonFilterInstance = this.gridApi.getFilterInstance('basisPreis');
      if (this.inputMaxBudgetProPerson) {
        maxBudgetProPersonFilterInstance.setModel({
          type: 'lessThanOrEqual',
          filter: this.inputMaxBudgetProPerson,
          filterType: 'number',
        });
      } else {
        maxBudgetProPersonFilterInstance.setModel(null);
      }

      setTimeout(() => this.gridApi.onFilterChanged(), 100);
    },

    onRowClicked(row) {
      const isMasterRow = row?.node?.master;
      if (!isMasterRow) return;

      this.gridApi.setRowNodeExpanded(row.node, !row.node.expanded);
    },
    onReload() {
      this.gridApi.onFilterChanged();
    },
    ausgebuchteTermineChanged() {
      this.gridApi.onFilterChanged();
    },
    resetAllFilters() {
      this.gridApi.setFilterModel(null);
      this.regionSelected = [];
      this.countrySelected = [];
      this.dateAbflug = null;
      this.dateAnkunft = null;
      this.garantiertFilterSelected = null;
      this.zimmerkontingentBeruecksichtigen = false;
      this.rundAufenthaltsreiseSelected = null;
      this.inputReiseterminkuerzel = [];
      this.inputReisetitel = [];
      this.inputMaxBudgetProPerson = null;
      this.pax = 1;

      const currentHash = window.location.hash.split('?')[0];
      history.replaceState(null, null, currentHash);

      this.gridApi.onFilterChanged();
    },

    countriesFromStore() {
      if (this.countriesGroupedByRegions) {
        // Ein Land kann in mehreren Regionen liegen, daher distinct der values
        return [
          ...new Set(
            Object.values(this.countriesGroupedByRegions)
              .flat()
              .map(item => item.country)
          ),
        ];
      } else return [];
    },
    regionsFromStore() {
      if (this.countriesGroupedByRegions) {
        return Object.keys(this.countriesGroupedByRegions);
      } else return [];
    },
    filterAnwenden() {
      this.applyCurrentFiltersToGrid();

      const filtersApplied = Object.keys(this.gridApi.getFilterModel());
      applicationInsights.trackEventWithRouteAndUser(
        { name: 'Applied Filter Reisefinder' },
        {
          filters: filtersApplied,
          roles: this.parsedRoles,
          currentUserName: this.currentUserName,
        }
      );

      this.$bvModal.hide('modal-reisefinder');
      this.updateUrlWithFilters();
    },
    setRegionFilter(region) {
      this.regionSelected = region;
    },
    setCountryFilter(country) {
      this.countrySelected = country;
    },
    handleKeyDown(event) {
      // Check if the event's key is "k" and the appropriate modifier key is pressed
      if (event.key === 'k' && (event.metaKey || (event.ctrlKey && !event.metaKey))) {
        // Handle the Cmd + K (or Ctrl + K on Windows) event here
        this.filterActive = !this.filterActive;
        if (this.filterActive) {
          this.$bvModal.show('modal-reisefinder');
        } else {
          this.$bvModal.hide('modal-reisefinder');
        }
      }
      if (event.key === 'Escape' && this.checkIfFilterSupplied) {
        this.resetAllFilters();
      }
      if (
        event.key === 'Enter' &&
        (event.metaKey || (event.ctrlKey && !event.metaKey)) &&
        this.filterActive
      ) {
        this.$bvModal.hide('modal-reisefinder');
        this.filterAnwenden();
      }
    },
    onClickFilterButton() {
      this.filterActive = !this.filterActive;
      if (this.filterActive) {
        this.$bvModal.show('modal-reisefinder');
      }
    },
    // Beim Öffnen des Filters wird die Reisekürzelsuche geöffnet
    focusReisekuerzelFilter() {
      if (this.$refs.reisekuerzelFilter) {
        this.$refs.reisekuerzelFilter.open();
      }
    },
    // Beim Öffnen des Modals wird der aktuelle Filterstand gesichert, damit Abbrechen ihn wiederherstellen kann
    merkeFilterStand() {
      this.filterStandBeimOeffnen = {
        inputReiseterminkuerzel: [...this.inputReiseterminkuerzel],
        inputReisetitel: [...this.inputReisetitel],
        dateAbflug: this.dateAbflug,
        dateAnkunft: this.dateAnkunft,
        regionSelected: [...this.regionSelected],
        countrySelected: [...this.countrySelected],
        rundAufenthaltsreiseSelected: this.rundAufenthaltsreiseSelected,
        garantiertFilterSelected: this.garantiertFilterSelected,
        pax: this.pax,
        inputMaxBudgetProPerson: this.inputMaxBudgetProPerson,
        zimmerkontingentBeruecksichtigen: this.zimmerkontingentBeruecksichtigen,
      };
    },
    // Abbrechen verwirft alle Änderungen seit dem Öffnen und schließt das Modal
    filterAbbrechen() {
      if (this.filterStandBeimOeffnen) {
        Object.assign(this, this.filterStandBeimOeffnen);
      }
      this.$bvModal.hide('modal-reisefinder');
    },
    stepPax(delta) {
      this.pax = Math.max(1, (Number(this.pax) || 1) + delta);
    },
    stepBudget(delta) {
      const budget = (Number(this.inputMaxBudgetProPerson) || 0) + delta;
      this.inputMaxBudgetProPerson = budget > 0 ? budget : null;
    },
    // Beim Weitertabben im Filter klappt ein erreichtes AppSelect direkt auf
    onFilterTabKeydown(event) {
      if (event.key !== 'Tab' || event.ctrlKey || event.metaKey || event.altKey) {
        return;
      }
      requestAnimationFrame(() => openAppSelectTrigger(document.activeElement));
    },
    dateFormatterStandard(params) {
      const abfragedatum = params.value;
      if (!abfragedatum) return '';
      const parsedDate = parseISO(abfragedatum);
      const formattedDate = format(parsedDate, 'dd.MM.yyyy');
      return formattedDate;
    },
    updateStatusBar(displayedRowsCount) {
      const statusBarComponent = this.gridApi?.getStatusPanel('statusBarCompKey');
      statusBarComponent?.setRowCount(displayedRowsCount);
      statusBarComponent?.setOdataCount(this.count);
    },
    createDatasource(server) {
      return {
        getRows: async params => {
          const response = await server.getData(params.request);
          if (response.success) {
            params.success({
              rowData: response.rows,
            });
            this.updateStatusBar(this.gridApi?.getDisplayedRowCount());
          } else {
            params.fail();
          }
        },
      };
    },
    onGridReady(params) {
      this.gridApi = params.api;
      this.gridColumnApi = params.columnApi;
      const updateData = () => {
        const server = this.server();
        const datasource = this.createDatasource(server);
        this.gridApi.setServerSideDatasource(datasource);
      };
      updateData();

      this.$nextTick(() => {
        if (this.checkIfFilterSupplied) {
          this.applyCurrentFiltersToGrid();
        }
      });
    },
    server() {
      return {
        getData: async request => {
          const enhancedRequest = {
            ...request,
            interessenten: this.pax,
            zimmerkontingentBeruecksichtigen: this.zimmerkontingentBeruecksichtigen,
            garantiertFilterSelected: this.garantiertFilterSelected,
            ausgebuchteTermineAnzeigen: this.ausgebuchteTermineCheckbox,
            maxBudget: this.inputMaxBudgetProPerson,
          };
          console.log('[Datasource] - rows requested by grid: ', enhancedRequest);
          const response = await this.$store.dispatch(GET_REISETERMINE, enhancedRequest);
          return {
            success: true,
            rows: response,
          };
        },
      };
    },
    async getReisekuerzel() {
      let query = buildQuery({
        select: ['reisekuerzel', 'id'],
        filter: { reisetermine: { any: { startdatum: { ge: new Date() } } } },
        count: true,
      });
      this.reiseterminSearchLoading = true;
      await apiService
        .get('Reise' + query)
        .then(res => {
          this.allReisekuerzel = (res?.data?.result?.value ?? []).map(reise => reise.reisekuerzel);
          this.reisekuerzelOptions = this.allReisekuerzel;
        })
        .catch(err => {
          console.err(err);
        });
      this.reiseterminSearchLoading = false;
      this.reisekuerzelOptions = this.allReisekuerzel;
    },
    onSearchChangeReisetermin($event) {
      if ($event.length > 6) {
        this.reiseterminSearchLoading = true;

        if (this.timeoutReiseterminkuerzelSearch) {
          clearTimeout(this.timeoutReiseterminkuerzelSearch);
        }
        this.timeoutReiseterminkuerzelSearch = setTimeout(() => {
          apiService
            .get(
              `Reisetermin?$filter=startswith(reiseterminkuerzel,'${$event}') and abreisedatum ge ${format(
                new Date(),
                'yyyy-MM-dd'
              )}&$select=reiseterminkuerzel,id`
            )
            .then(res => {
              this.reiseterminSearchLoading = false;
              const reisetermine = [...res.data.result.value] || [];
              this.reisekuerzelOptions = reisetermine.map(reisetermin => reisetermin.reiseterminkuerzel);
            })
            .catch(err => {
              console.error(err);
              this.reiseterminSearchLoading = false;
            });
        }, 500); // delay
      } else if (this.reisekuerzelOptions.length !== this.allReisekuerzel.length) {
        this.reisekuerzelOptions = this.allReisekuerzel;
      }
    },
    // onSearchChangeReisetitel($string) {
    //   if ($string.length > 0) {
    //     this.reisetitelSearchLoading = true;
    //     this.debouncedSearchReisetitel($string);
    //   } else {
    //     this.reisetitelSearchLoading = false;
    //     this.reisetitelOptions = [];
    //   }
    // },

    onSearchChangeReisetitel($string) {
      if ($string.length > 0) {
        this.reisetitelSearchLoading = true;

        const split = $string.split(' ');
        const condition = split.map(term => ({
          titel: {
            contains: term,
          },
        }));

        const filter = {
          and: condition,
        };
        const queryString = buildQuery({ filter });

        this.debouceTest(queryString);
      }
    },

    changeReisekuerzel($event) {
      this.inputReiseterminkuerzel = $event;
    },
    changeReisetitel($event) {
      this.inputReisetitel = $event;
    },
    getRowHeight(params) {
      return params.data
        ? Math.max(
            Math.ceil(params.data.zimmerkontingente?.filter(z => z.isDeleted === false).length / 5) * 45,
            45
          )
        : 45;
    },
    getEinzelzimmerzuschlag(data) {
      const ezKontingent = data.zimmerkontingente.find(
        zimmerkontingent => zimmerkontingent.zimmertyp === 'EZ'
      );
      if (ezKontingent) {
        return this.$options.filters.currency(ezKontingent.zuschlag);
      } else return '-';
    },

    calcTageBisRealease(releasefristenArray, startdatum) {
      if (!releasefristenArray || !startdatum) return '-';

      if (releasefristenArray.length > 0) {
        const berechnungTage = releasefrist => {
          const releaseDate = subDays(new Date(startdatum), releasefrist);

          return differenceInCalendarDays(releaseDate, new Date());
        };

        return releasefristenArray.map(berechnungTage).join(' / ');
      } else {
        return '-';
      }
    },
    calculateReleaseDays(params) {
      const { startdatum, metadata } = params.data || {};

      if (new Date(startdatum).getFullYear() >= 2025) {
        return metadata?.releasefrist ? this.calcTageBisRealease(metadata.releasefrist, startdatum) : '-';
      }

      return differenceInDays(parseISO(params.value), new Date());
    },
  },
};
</script>
<style lang="scss" scoped>
@import '~ag-grid-community/styles/ag-grid.css';
@import '~ag-grid-community/styles/ag-theme-alpine.css';
:deep(.ag-theme-alpine) {
  font-family: 'Poppins';
}
:deep(.ag-cell-value) {
  overflow: visible;
}
:deep(.ag-theme-alpine .ag-header-cell) {
  font-weight: 600;
  font-size: 14px;
}
.inputReiseterminkuerzel::placeholder {
  color: #adadad;
}
.ag-icon-filter {
  display: none;
}
:deep(.ag-row-group-expanded) {
  background-color: #e5f0fb;
}

.reisefinder-filter {
  --app-select-valid-border-color: var(--primary);
  --app-date-picker-valid-border-color: var(--primary);
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.reisefinder-filter__zeile {
  display: flex;
  gap: 12px;
}
.reisefinder-filter__zeile > * {
  flex: 1;
  min-width: 0;
}
.reisefinder-stepper {
  display: flex;
  align-items: center;
  flex: 1;
  height: var(--control-h);
  border: 1px solid var(--border);
  border-radius: var(--radius-sm);
  background: var(--surface);
}
.reisefinder-stepper--aktiv {
  border-color: var(--primary);
}
.reisefinder-stepper:focus-within {
  border-color: var(--primary);
  box-shadow: var(--focus-ring);
}
.reisefinder-stepper__btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 100%;
  border: none;
  background: transparent;
  color: var(--text-muted);
  cursor: pointer;
}
.reisefinder-stepper__btn:hover {
  color: var(--text);
}
.reisefinder-stepper__input {
  height: 100%;
  border: none;
  text-align: center;
  -moz-appearance: textfield;
  appearance: textfield;
}
/* Native Pfeile des Zahlenfelds ausblenden, die -/+ Buttons übernehmen diese Funktion */
.reisefinder-stepper__input::-webkit-outer-spin-button,
.reisefinder-stepper__input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
.reisefinder-stepper__schnellwahl {
  width: var(--control-h);
  height: var(--control-h);
  padding: 0;
}
.reisefinder-switch-box {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: var(--control-h);
  padding: 0 16px;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius-sm);
  color: var(--text-body);
  cursor: pointer;
}
.reisefinder-switch-box--aktiv {
  border-color: var(--primary);
}
.reisefinder-switch-box__text {
  flex: 1;
  margin: 0;
  cursor: pointer;
}
.reisefinder-switch-box :deep(.custom-switch) {
  margin: 0;
}

/* Dropdown-Panel des Reisetitel-Filters breiter als das Control selbst */
.reisetitel-select {
  --app-select-menu-max-width: 380px;
  --app-select-option-white-space: normal;
}
</style>
<style>
.reisefinder-info-tooltip {
  opacity: 1 !important;
}
</style>
